import React from "react";
import { useStoreState } from "easy-peasy";
import moment from "moment";
import { intl } from "LocaleWrapper";

import { Box, CircularProgress, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CallMergeIcon from "@mui/icons-material/CallMerge";

import useLayout from "components/layout/useLayout";
import { useConfirm } from "components/hooks/useConfirm";

import { calculateCallStatus } from "view/calls/utils/CallUtils";
import CallSideviewer from "view/calls/components/sideviewer/CallSideviewer";

import { hasPluginPermission, shortid, DataFormat } from "imports/Utils";

import { CapacityEnum } from "./ControlTowerBoard";
import { useLazyQuery } from "@apollo/client";
import { GetCallDocument } from "__generated__/view/calls/graphql/call.queries.generated";

export function filterShipPlanning(resources, from, to) {

	const currentResources = [ ...resources ].map(item => ({
		...item,
		voyages: item.voyages.map(voyage => ({
			...voyage,
			calls: [ ...voyage.calls ].filter((call, index) => {

				const prevCall = voyage.calls[index - 1];
				const nextCall = voyage.calls[index + 1];

				return (
					(nextCall && (from?.id === call.location?.id) && (to ? to?.id === nextCall?.location?.id : true)) ||
					(prevCall && (to?.id === call.location?.id) && (from ? from?.id === prevCall?.location?.id : true)) ||
					(!from && !to)
				);
			}),
		})),
	}));

	const createGroup = (calls) => [ ...calls ].map(c => c.id).sort().join("/");
	const twinCallGroups = [];

	const mergedCalls = [ ...currentResources ].map(item => ({
		...item,
		voyages: item.voyages.map(voyage => ({
			...voyage,
			calls: [ ...voyage.calls ]

				//temporary workaround to handle the correct call order
				.sort((a, b) => {
					return a.pta - b.pta;
				})

				.map((call) => {

					const allCalls = [ ...item.voyages ].flatMap(v => v.calls).filter(c => c.id !== call.id);
					const sameCalls = [ ...allCalls ].filter(c => (c.pta === call.pta) && (c.location?.id === call.location?.id));

					if (sameCalls.length > 0) {

						const twinnedCalls = [ call, ...sameCalls ];
						const isMerged = twinCallGroups.includes(createGroup(twinnedCalls));

						if (!isMerged) {
							twinCallGroups.push(createGroup(twinnedCalls));
						}

						return {
							...call,
							isTwinCall: true,
							twinnedCalls: twinnedCalls,
						};
					} else {
						return call;
					}
				}),
		})),
	}));

	return mergedCalls;
}

const getStartTimeFromCall = (call, isPlanned) => {
	return isPlanned ? call.pta : getActualArrivalTime(call);
};

const getEndTimeFromCall = (call, isPlanned) => {

	let end = isPlanned ? call.ptd : getActualDepartureTime(call);
	const now = moment().unix() * 1000;

	if (!isPlanned && call.ata && !call.atd && end < now) {
		end = now;
	}

	return end;
};

const getActualArrivalTime = call => {
	return call.ata || call.systemAta || call.eta || call.systemEta || call.pta;
};

const getActualDepartureTime = call => {
	return call.atd || call.systemAtd || call.etd || call.systemEtd || call.ptd;
};

export const createCharterItem = (charter, group) => {
	return {
		id: shortid.generate(),
		entity: charter,
		group: group,
		start: moment(charter.validFrom),
		end: moment(charter.validTo),
		className: "charter",
		title: "",
	};
};

export const createVoyageTask = (voyage, group, filter, isPlanned, refetch) => {

	const calls = voyage?.calls || [];
	const voyageStart = calls.length > 0 ? (getStartTimeFromCall(calls[0], isPlanned)) : voyage.start;
	const voyageEnd = calls.length > 0 ? (getEndTimeFromCall(calls[calls.length - 1], isPlanned)) : voyage.start;
	const voyageLengthInMinutes = moment(voyageEnd).diff(moment(voyageStart), "minutes");

	return {
		id: shortid.generate(),
		entity: voyage,
		group: group,
		start: moment(voyageStart),
		end: moment(voyageEnd),
		className: "voyage",
		title: (
			<div style={{ display: "flex", width: "100%" }}>
				{calls
					.sort((a, b) => getStartTimeFromCall(a, isPlanned) - getStartTimeFromCall(b, isPlanned))
					.map((call, index) => {
						return (
							<React.Fragment key={index}>
								<CallTask
									call={call}
									voyageLengthInMinutes={voyageLengthInMinutes}
									isPlanned={isPlanned}
									filter={filter}
									refetch={refetch} />

								{calls[index + 1] && (
									<CapacityTask
										voyage={voyage}
										startCall={call}
										endCall={calls[index + 1]}
										voyageLengthInMinutes={voyageLengthInMinutes}
										isPlanned={isPlanned}
										capacity={filter.capacity}
										ship={voyage.ship} />
								)}
							</React.Fragment>
						);
					})}
			</div>
		),
	};
};

const CallTask = ({ call, voyageLengthInMinutes, isPlanned, filter, refetch }) => {

	const session = useStoreState(state => state.session);
	const layout = useLayout();

	const start = getStartTimeFromCall(call, isPlanned);
	const end = getEndTimeFromCall(call, isPlanned);
	const callLengthInMinutes = moment(end).diff(moment(start), "minutes");
	const width = (callLengthInMinutes / voyageLengthInMinutes) * 100;
	const calculatedStatus = calculateCallStatus(call, session);
	const color = isPlanned ? call.lastStatusChange?.status?.color : calculatedStatus.color;

	const [ getCall ] = useLazyQuery(GetCallDocument);

	const handleOnClick = () => {
		layout.setDrawerSettings({
			open: true,
			title: intl.formatMessage({ id: "CALLS" }),
			content: (
				<Box p={2}>
					<CircularProgress size={24} />
				</Box>
			),
			size: "normal",
			variant: "overlay",
		});

		getCall({
			variables: {
				id: call.id,
			},
		}).then(({ data }) => {
			layout.setDrawerSettings((prev) => ({
				...prev,
				content: (
					<CallSideviewer
						defaultCallId={data?.call.voyage.calls?.[0]?.id ?? null}
						nextCallId={call.id}
						refetch={() => refetch()}
						bargeType={null}
						isShipEditable={false} />
				),
			}));
		});
	};

	const createTitle = () => {

		const { location, cargos, id, weight, volume, lastStatusChange, eta, systemEta } = call;

		const code = location?.displayCode || location?.code;
		const description = hasPluginPermission(session, "boardShowLocationDescription") ? (location?.description || code) : (code || location?.description);
		const cargoAction = cargos?.find(c => c.call?.id === id);

		const fields = [ description ];

		if (cargoAction?.cargo?.product) {
			fields.push(cargoAction.cargo.product.name);
		}

		fields.push(DataFormat.tonnage(weight || 0));
		fields.push(DataFormat.volumeM3(volume || 0));

		if (cargoAction?.cargo?.supplier) {
			fields.push(cargoAction.cargo.supplier.name);
		}

		if (lastStatusChange?.status?.description) {
			fields.push(lastStatusChange?.status?.description);
		}

		if (eta || systemEta) {
			fields.push(intl.formatMessage({ id: "ETA" }) + " " + moment(eta || systemEta).format("lll").replace(/\d{4,6}/, ""));
		}

		return filter.detailed ? fields.join(" - ") : description;
	};

	const title = createTitle();
	const inPast = end < moment().unix() * 1000;
	const noActualTime = !isPlanned && !call.eta && !call.ata;
	const highlight = call.location?.id === filter?.location?.id;

	return (
		<div
			id={`call-task-${call.id}`}
			className={`call-task ${inPast ? "in-past" : ""} ${noActualTime ? "no-actual-time" : ""} ${highlight ? "highlight" : ""} ${filter.abbreviateText ? "abbreviateText" : ""}`}
			onClick={handleOnClick}
			title={title}
			style={{ backgroundColor: color, width: `${width}%` }}>
			<React.Fragment>
				<div className="text">
					{title}
				</div>

				{call.isTwinCall && (
					<span
						className="twin-call"
						onMouseOver={() => {
							const otherCall = call.twinnedCalls.find(it => it.id !== call.id);
							document.getElementById(`call-task-${otherCall.id}`).classList.add("showUp");
						}}
						onMouseLeave={() => {
							const otherCall = call.twinnedCalls.find(it => it.id !== call.id);
							document.getElementById(`call-task-${otherCall.id}`).classList.remove("showUp");
						}}>
						<CallMergeIcon />
					</span>
				)}
			</React.Fragment>
		</div>
	);
};

const CapacityTask = ({ voyage, startCall, endCall, voyageLengthInMinutes, isPlanned, capacity, ship }) => {

	const session = useStoreState(state => state.session);
	const useContainers = hasPluginPermission(session, "useContainers");

	const start = getEndTimeFromCall(startCall, isPlanned);
	const end = getStartTimeFromCall(endCall, isPlanned);

	const capacityTaskLengthInMinutes = moment(end).diff(moment(start), "minutes");
	const width = (capacityTaskLengthInMinutes / voyageLengthInMinutes) * 100;

	const remainingCapacity = (capacity === CapacityEnum.TEU ? startCall.remainingTeuCapacity : startCall.remainingWeightCapacity) || 0;
	const totalPlanned = (capacity === CapacityEnum.TEU ? startCall.loadedTeuOnVoyageAfterCall : startCall.loadedWeightOnVoyageAfterCall) || 0;
	const totalCapacity = (remainingCapacity + totalPlanned) || (capacity === CapacityEnum.TEU ? ship.capacityTeu : ship.capacityTon) || 0;
	const progress = totalPlanned > 0 ? totalPlanned / totalCapacity * 100 : 0.00001;
	const animation = { width: `${progress}%` };
	const content = `${totalPlanned.toFixed(2) * 1} / ${totalCapacity.toFixed(2) * 1} ${capacity === CapacityEnum.TEU ? DataFormat.UNITS.TEU : DataFormat.UNITS.MTON}`;
	const inPast = end < moment().unix() * 1000;

	return (
		<div className={`capacity-task ${inPast ? "in-past" : ""}`} style={{ background: "red", width: `${width}%` }}>
			<div className="progress" title={content}>
				<div className="fill" style={animation} />

				<div className="content">
					<div className="text">
						{useContainers ? (
							<React.Fragment>
								{voyage?.voyageNumber}
							</React.Fragment>
						) : (
							<React.Fragment>
								{`${content} (${voyage?.voyageNumber})`}
							</React.Fragment>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export const createEditableVoyageTask = (voyage, group, onDelete) => {

	const createTitle = () => {
		if (voyage?.calls?.[0]) {
			return `${voyage.voyageNumber} - ${voyage.calls[0].location?.description} => ${voyage.calls[voyage.calls.length - 1].location?.description}`;
		} else {
			return voyage.voyageNumber;
		}
	};

	const title = createTitle();

	return {
		id: voyage.id,
		entity: voyage,
		group: group,
		start: moment(voyage.start),
		end: moment(voyage.end),
		className: "voyage-editable",
		canMove: true,
		canResize: "both",
		title: (
			<div className="voyage-editable-content">
				<div className="text">
					{title}
				</div>

				{onDelete && (
					<TaskDeleteButton onDelete={() => onDelete(voyage)} />
				)}
			</div>
		),
	};
};

const TaskDeleteButton = ({ onDelete }) => {

	const confirm = useConfirm();

	return (
		<span className="delete-button">
			<IconButton
				size="small"
				onClick={() => {
					confirm({
						type: "DELETE",
					}).then(() => {
						onDelete();
					});
				}}>
				<DeleteIcon fontSize="small" />
			</IconButton>
		</span>
	);
};
